import { useCallback, useEffect, useRef, useState } from "react";
import {
  type BuyerBidV2DetailDto,
  type BuyerQualificationReviewResponsePatchDto,
  useUpdateBuyerQualificationReviewMutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";

const DEBOUNCE_MS = 300;

interface EditorState {
  verdicts: Record<string, boolean | null>;
  comments: Record<string, string>;
}

interface UseEditor {
  state: EditorState;
  setVerdict: (criterionKey: string, value: boolean | null) => void;
  setComment: (criterionKey: string, value: string) => void;
  isSaving: boolean;
}

export const useBuyerQualificationReviewEditor = (
  eventId: string,
  bidId: string,
  detail: BuyerBidV2DetailDto,
): UseEditor => {
  const { trigger } = useApiError();
  const [update, { isLoading: isSaving }] = useUpdateBuyerQualificationReviewMutation();
  const [state, setState] = useState<EditorState>(() => initialState(detail));
  const pendingByKey = useRef<Map<string, BuyerQualificationReviewResponsePatchDto>>(new Map());
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingRef = useRef(isSaving);
  savingRef.current = isSaving;

  useEffect(() => {
    if (pendingByKey.current.size > 0 || savingRef.current) return;
    setState(initialState(detail));
  }, [detail]);

  const flush = useCallback(async () => {
    if (pendingByKey.current.size === 0) return;
    const responses = Array.from(pendingByKey.current.values());
    pendingByKey.current.clear();
    try {
      await update({
        eventId,
        bidId,
        buyerQualificationReviewPatchDto: { responses },
      }).unwrap();
    } catch (e) {
      trigger(e);
    }
  }, [bidId, eventId, trigger, update]);

  const schedule = useCallback(() => {
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(() => {
      flushTimer.current = null;
      void flush();
    }, DEBOUNCE_MS);
  }, [flush]);

  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(
    () => () => {
      if (flushTimer.current) clearTimeout(flushTimer.current);
      void flushRef.current();
    },
    [],
  );

  const enqueueVerdict = useCallback(
    (criterionKey: string, value: boolean | null) => {
      const current = pendingByKey.current.get(criterionKey) ?? { criterionKey };
      pendingByKey.current.set(criterionKey, { ...current, buyerVerdict: value });
      schedule();
    },
    [schedule],
  );

  const enqueueComment = useCallback(
    (criterionKey: string, value: string) => {
      const current = pendingByKey.current.get(criterionKey) ?? { criterionKey };
      pendingByKey.current.set(criterionKey, { ...current, buyerComment: value });
      schedule();
    },
    [schedule],
  );

  const setVerdict = useCallback(
    (criterionKey: string, value: boolean | null) => {
      setState((s) => ({ ...s, verdicts: { ...s.verdicts, [criterionKey]: value } }));
      enqueueVerdict(criterionKey, value);
    },
    [enqueueVerdict],
  );

  const setComment = useCallback(
    (criterionKey: string, value: string) => {
      setState((s) => ({ ...s, comments: { ...s.comments, [criterionKey]: value } }));
      enqueueComment(criterionKey, value);
    },
    [enqueueComment],
  );

  return {
    state,
    setVerdict,
    setComment,
    isSaving,
  };
};

const initialState = (detail: BuyerBidV2DetailDto): EditorState => {
  const verdicts: Record<string, boolean | null> = {};
  const comments: Record<string, string> = {};
  for (const r of detail.bid.qualificationResponses ?? []) {
    verdicts[r.criterionKey] = r.buyerVerdict ?? null;
    comments[r.criterionKey] = r.buyerComment ?? "";
  }
  return { verdicts, comments };
};
